/**
 * @module builder
 */
import type { OptionsDocument, PresentationSection } from 'paintscout'
import { getPresentationSectionOptions } from '../get-presentation-section-options'

/**
 * Resolve a section's display label and title from the current options definitions
 * (looked up by section.type). Falls back to the values stored on the section so
 * custom sections and unknown types still render reasonably.
 *
 * Why: section.label / section.title are copied onto the section document at creation
 * time and frozen there. When a section type is renamed in the options file, existing
 * sections continue to display the old text until something resolves it from the
 * current options. This helper does that resolution.
 */
export function getPresentationSectionLabel(args: {
  section: Pick<PresentationSection, 'type' | 'label' | 'title'> | null | undefined
  options?: OptionsDocument
}): { label: string; title: string } {
  const { section, options } = args

  if (!section) {
    return { label: '', title: '' }
  }

  const fallbackLabel = section.label ?? ''
  const fallbackTitle = section.title ?? fallbackLabel

  if (!section.type) {
    return { label: fallbackLabel, title: fallbackTitle }
  }

  const sectionOptions = getPresentationSectionOptions({ options, skipSort: true })
  const match = sectionOptions.find((opt) => opt.type === section.type)

  if (!match) {
    return { label: fallbackLabel, title: fallbackTitle }
  }

  return {
    label: match.label ?? fallbackLabel,
    title: match.title ?? match.label ?? fallbackTitle
  }
}
